import { toast } from "react-toastify";
import { getEos } from "./eosconfig";
import {
  CONTRACT_NAME,
  ESCROW_CONTRACT_NAME,
  TOKEN_CONTRACT,
} from "../config/misc";
import { handleTxCallError } from "../utils/utils";
import ToastifyIconComponent from "../components/Common/ToastifyIconComponent";
import { ethers } from 'ethers';
import { CONTRACTS, RPC_URLS, CHAIN_ID } from '../utils/contractAddresses';

/**
 * Run a read against each REACT_APP_RPC_URLS endpoint in turn until one
 * answers. A single free endpoint is not something a balance check before a
 * payment should depend on. `staticNetwork` skips ethers' network detection,
 * which otherwise retries forever against an endpoint that is down, and the
 * per-request timeout replaces ethers' default of five minutes.
 */
const RPC_TIMEOUT_MS = 5000;
const withReadProvider = async (read) => {
  let lastError = new Error("No RPC endpoint configured (REACT_APP_RPC_URLS)");
  for (const url of RPC_URLS) {
    const request = new ethers.FetchRequest(url);
    request.timeout = RPC_TIMEOUT_MS;
    const provider = new ethers.JsonRpcProvider(request, CHAIN_ID, { staticNetwork: true });
    try {
      return await read(provider);
    } catch (err) {
      lastError = err;
      console.warn(`RPC read failed on ${url}, trying next endpoint`, err?.shortMessage || err?.message);
    } finally {
      provider.destroy();
    }
  }
  throw lastError;
};

// Basic ERC-20 ABI for DAI token
const ERC20_ABI = [
  {
    "constant": true,
    "inputs": [{ "name": "_owner", "type": "address" }],
    "name": "balanceOf",
    "outputs": [{ "name": "balance", "type": "uint256" }],
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [],
    "name": "decimals",
    "outputs": [{ "name": "", "type": "uint8" }],
    "type": "function"
  },
  {
    "constant": false,
    "inputs": [
      { "name": "_to", "type": "address" },
      { "name": "_value", "type": "uint256" }
    ],
    "name": "transfer",
    "outputs": [{ "name": "", "type": "bool" }],
    "type": "function"
  }
];

const MIDL_CONTRACT = "midl";
const FEE_CONTRACT_NAME = "midl.token";
const JURY_CONTRACT = "midl.jury";
const TRIBUNAL_ALACRITY_NAME = "Adil";

// Updated for Polygon integration with DAI token balance checking
export const getUserBalance = async (walletAddressOrUsername, rpc = null) => {
  try {
    // Handle both old (username, rpc) and new (walletAddress) signatures
    const walletAddress = walletAddressOrUsername;

    // Balance and decimals from the DAI contract, on the first endpoint that answers
    const { balance, decimals } = await withReadProvider(async (provider) => {
      const daiTokenContract = new ethers.Contract(CONTRACTS.daiToken, ERC20_ABI, provider);
      const balance = await daiTokenContract.balanceOf(walletAddress);
      // Get token decimals (DAI typically has 18 decimals)
      const decimals = balance === 0n ? null : await daiTokenContract.decimals();
      return { balance, decimals };
    });

    console.log("DAI balance", balance);
    if (balance === 0n) {
      toast.error(<div className="content">Insufficient balance: 0 DAI</div>, {
        icon: () => (<ToastifyIconComponent />),
        className: "midl-toast",
      });
      return 0;
    }

    // Convert from wei to token units using actual decimals
    const balanceFormatted = ethers.formatUnits(balance, decimals);
    return parseFloat(balanceFormatted);
  } catch (err) {
    console.log('Error getting user balance:', err);
    toast.error(<div className="content">Unable to fetch your DAI balance, try again</div>, {
      icon: () => (<ToastifyIconComponent />),
      className: "midl-toast",
    });
    return null;
  };
}


export const getUserCollateralBalance = async (walletAddress) => {
  try {
    // Input validation
    if (!walletAddress || !ethers.isAddress(walletAddress)) {
      console.error("Invalid wallet address provided");
      return null;
    }

    console.log(`Fetching collateral for address: ${walletAddress}`);

    // Use the correct ABI with getCollateral function
    const frdmRouterAbi = [
      {
        "type": "function",
        "name": "getCollateral",
        "constant": true,
        "stateMutability": "view",
        "payable": false,
        "inputs": [
          {
            "type": "address",
            "name": "user"
          }
        ],
        "outputs": [
          {
            "type": "tuple",
            "components": [
              {
                "type": "uint256",
                "name": "tiedAmount"
              },
              {
                "type": "uint256",
                "name": "freeAmount"
              },
              {
                "type": "uint256",
                "name": "lockAmount"
              },
              {
                "type": "uint256",
                "name": "totalDeposit"
              },
              {
                "type": "uint256",
                "name": "businessLimit"
              },
              {
                "type": "bool",
                "name": "isUserExist"
              }
            ]
          }
        ]
      }
    ];

    // Deployment check and getCollateral, on the first endpoint that answers
    const collateralData = await withReadProvider(async (provider) => {
      const frdmRouterContract = new ethers.Contract(
        CONTRACTS.frdmRouter,
        frdmRouterAbi,
        provider
      );

      // Check if contract is deployed
      try {
        const code = await provider.getCode(CONTRACTS.frdmRouter);
        if (code === '0x') {
          console.error("Contract not deployed at address:", CONTRACTS.frdmRouter);
          throw new Error("Contract not deployed");
        }
        console.log("Contract is deployed");
      } catch (contractError) {
        console.error("Error checking contract deployment:", contractError);
        throw new Error("Contract not accessible");
      }

      // Call getCollateral instead of collateralOf
      let collateralData;
      try {
        collateralData = await frdmRouterContract.getCollateral(walletAddress);
        console.log("Raw collateral data:", collateralData);
      } catch (callError) {
        console.error("getCollateral call failed:", callError);

        if (callError.revert?.args) {
          console.error("Revert args:", callError.revert.args);
        }

        if (callError.code === 'CALL_EXCEPTION') {
          // Check if user doesn't exist in the system
          throw new Error("User collateral data not found");
        } else {
          throw callError;
        }
      }
      return collateralData;
    });

    // Destructure the response tuple
    const [tiedAmount, freeAmount, lockAmount, totalDeposit, businessLimit, isUserExist] = collateralData;

    console.log(`Collateral details for ${walletAddress}:`, {
      tiedAmount: ethers.formatUnits(tiedAmount, 18),
      freeAmount: ethers.formatUnits(freeAmount, 18),
      lockAmount: ethers.formatUnits(lockAmount, 18),
      totalDeposit: ethers.formatUnits(totalDeposit, 18),
      businessLimit: ethers.formatUnits(businessLimit, 18),
      isUserExist: isUserExist
    });

    // Use totalDeposit as the main collateral balance (or choose what fits your use case)
    const collateralFormatted = ethers.formatUnits(totalDeposit, 18);
    const collateralValue = parseFloat(collateralFormatted);

    console.log(`Total collateral balance for ${walletAddress}: ${collateralValue} DAI`);

    return collateralValue;

  } catch (err) {
    console.error("Error getting user collateral balance:", err);

    let errorMessage = "Unable to fetch your collateral balance, try again";

    if (err.code === 'NETWORK_ERROR' || err.code === 'TIMEOUT') {
      errorMessage = "Network error. Please check your connection and try again";
    } else if (err.code === 'CALL_EXCEPTION') {
      if (err.message.includes("Delegate call failed")) {
        errorMessage = "Contract configuration error. Please contact support.";
      } else {
        errorMessage = "Unable to read collateral data from blockchain";
      }
    } else if (err.message.includes("Contract not deployed")) {
      errorMessage = "Contract not found at specified address";
    } else if (err.message.includes("User collateral data not found")) {
      errorMessage = "No collateral found for this address";
      // You might want to return 0 instead of showing error for new users
      return 0;
    }

    toast.error(<div className="content">{errorMessage}</div>, {
      icon: () => (<ToastifyIconComponent />),
      className: "midl-toast",
    });

    return null;
  }
};

// Updated for Polygon integration
export const submitCollateral = async (amount, walletAddress) => {
  try {
    // For now, return a placeholder since this requires wallet interaction
    // In a real implementation, this would need to be called from a component
    // that has access to the wallet signer
    console.log('submitCollateral called with:', { amount, walletAddress });

    // TODO: Implement actual collateral submission with wallet interaction
    // This requires the component to have access to the wallet signer
    // and should be moved to a component that can handle wallet interactions

    return { success: true, message: 'Collateral submission not yet implemented for Polygon' };
  } catch (err) {
    console.error("error on submit collateral", err);
    handleTxCallError(err);
  }
};

export const removeCollateral = async (opns, rpc, username) => {
  const { eos } = rpc;
  const transactionData = {
    actions: [
      {
        account: CONTRACT_NAME,
        name: "rmcoll",
        authorization: [
          {
            actor: username,
            permission: "active",
          },
        ],
        data: {
          from: username,
          quantity: `${opns?.toFixed(4)} OPN`,
          memo: "",
        },
      },
    ],
  };
  try {
    const result = await eos.transaction(transactionData);
    if (result.processed.receipt.status == "executed") {
      return result;
    } else return null;
  } catch (err) {
    console.error("error", err);
    handleTxCallError(err);
  }
};

// Updated for Polygon integration - DAI token approval to FRDM Router
export const approveDaiToRouter = async (amount, walletAddress, walletClient) => {
  console.log("Approving DAI amount:", amount, "walletAddress", walletAddress);

  try {
    if (!walletClient) {
      throw new Error("Wallet client is required for token approval");
    }

    // DAI token has 18 decimals, so convert amount to wei
    const amountInWei = Math.floor(amount * 1e18).toString();

    // Prepare the approval data using wagmi's contract interface
    const approvalData = walletClient.encodeContractFunctionData({
      abi: ERC20_ABI,
      functionName: 'approve',
      args: [CONTRACTS.frdmRouter, amountInWei]
    });

    // Execute the approval using wallet client
    const tx = await walletClient.request({
      method: 'eth_sendTransaction',
      params: [{
        from: walletAddress,
        to: CONTRACTS.daiToken,
        data: approvalData,
        gas: '0x' + (100000).toString(16), // Gas limit for approval
      }]
    });

    console.log("DAI approval transaction submitted:", tx);

    // Wait for transaction confirmation using wagmi
    const receipt = await walletClient.waitForTransactionReceipt({
      hash: tx,
      confirmations: 1
    });

    if (receipt.status === 'success') {
      console.log("DAI approval successful:", receipt);
      return {
        success: true,
        transactionHash: receipt.transactionHash,
        blockNumber: receipt.blockNumber,
        gasUsed: receipt.gasUsed.toString()
      };
    } else {
      throw new Error("Approval transaction failed");
    }
  } catch (err) {
    console.error("error on DAI approval", err);
    handleTxCallError(err);
    return null;
  }
};

// Updated for Polygon integration - Call addCollateral on FRDM Router
export const addCollateralToRouter = async (amount, walletAddress, walletClient) => {
  console.log("Adding collateral amount:", amount, "walletAddress", walletAddress);

  try {
    if (!walletClient) {
      throw new Error("Wallet client is required for addCollateral call");
    }

    // DAI token has 18 decimals, so convert amount to wei
    const amountInWei = Math.floor(amount * 1e18).toString();

    // FRDM Router ABI for addCollateral method
    const FRDM_ROUTER_ABI = [
      {
        "type": "function",
        "name": "addCollateral",
        "constant": false,
        "payable": false,
        "inputs": [
          {
            "type": "uint256",
            "name": "amount"
          }
        ],
        "outputs": []
      }
    ];

    // Prepare the addCollateral data using wagmi's contract interface
    const addCollateralData = walletClient.encodeContractFunctionData({
      abi: FRDM_ROUTER_ABI,
      functionName: 'addCollateral',
      args: [amountInWei]
    });

    // Execute the addCollateral using wallet client
    const tx = await walletClient.request({
      method: 'eth_sendTransaction',
      params: [{
        from: walletAddress,
        to: CONTRACTS.frdmRouter,
        data: addCollateralData,
        gas: '0x' + (200000).toString(16), // Gas limit for addCollateral
      }]
    });

    console.log("AddCollateral transaction submitted:", tx);

    // Wait for transaction confirmation using wagmi
    const receipt = await walletClient.waitForTransactionReceipt({
      hash: tx,
      confirmations: 1
    });

    if (receipt.status === 'success') {
      console.log("AddCollateral successful:", receipt);
      return {
        success: true,
        transactionHash: receipt.transactionHash,
        blockNumber: receipt.blockNumber,
        gasUsed: receipt.gasUsed.toString()
      };
    } else {
      throw new Error("AddCollateral transaction failed");
    }
  } catch (err) {
    console.error("error on addCollateral", err);
    handleTxCallError(err);
    return null;
  }
};

export const addJuryStakes = async (opns, rpc, username) => {
  const { eos } = rpc;
  try {
    const result = await eos.transaction({
      actions: [
        {
          account: TOKEN_CONTRACT,
          name: "transfer",
          authorization: [
            {
              actor: username,
              permission: "active",
            },
          ],
          data: {
            from: username,
            to: CONTRACT_NAME,
            quantity: `${opns?.toFixed(4)} OPN`,
            memo: "lockstake",
          },
        },
      ],
    });
    if (result.processed.receipt.status === "executed") {
      return result;
    }
  } catch (err) {
    console.error("error when add jury stakes", err);
    handleTxCallError(err);
  }
};

export const removeJuryStakes = async (opns, rpc, username) => {
  const { eos } = rpc;

  try {
    const result = await eos.transaction({
      actions: [
        {
          account: CONTRACT_NAME,
          name: "unlockstake",
          authorization: [
            {
              actor: username,
              permission: "active",
            },
          ],
          data: {
            from: username,
            quantity: `${opns?.toFixed(4)} OPN`,
            memo: "",
          },
        },
      ],
    });

    if (result.processed.receipt.status === "executed") {
      return result;
    }
  } catch (err) {
    console.error("error when remove jury stakes", err);
    handleTxCallError(err);
  }
};

export const deductUserDiputedFee = async (
  currenyAmount,
  privateKey,
  username,
  memo
) => {
  const { eos } = getEos(privateKey);

  return await eos.transaction({
    actions: [
      {
        account: FEE_CONTRACT_NAME,
        name: "transfer",
        authorization: [
          {
            actor: username,
            permission: "active",
          },
        ],
        data: {
          from: username,
          to: JURY_CONTRACT,
          quantity: currenyAmount,
          memo: memo,
        },
      },
    ],
  });
};

export const deductUserAppealFee = async (
  currenyAmount,
  rpc,
  username,
  memo
) => {
  const { eos } = rpc;

  return await eos.transaction({
    actions: [
      {
        account: FEE_CONTRACT_NAME,
        name: "transfer",
        authorization: [
          {
            actor: username,
            permission: "active",
          },
        ],
        data: {
          from: username,
          to: JURY_CONTRACT,
          quantity: currenyAmount,
          memo: memo,
        },
      },
    ],
  });
};

export const deductCustodianDiputedFee = async (
  currenyAmount,
  rpc,
  username,
  memo
) => {
  const { eos } = rpc;

  return await eos.transaction({
    actions: [
      {
        account: CONTRACT_NAME,
        name: "transfer",
        authorization: [
          {
            actor: username,
            permission: "active",
          },
        ],
        data: {
          from: username,
          to: JURY_CONTRACT,
          quantity: currenyAmount, //"1.0000 OPN"
          memo: memo,
        },
      },
    ],
  });
};

export const deductCustodianAppealFee = async (
  currenyAmount,
  rpc,
  username,
  memo
) => {
  const { eos } = rpc;

  return await eos.transaction({
    actions: [
      {
        account: CONTRACT_NAME,
        name: "transfer",
        authorization: [
          {
            actor: username,
            permission: "active",
          },
        ],
        data: {
          from: username,
          to: JURY_CONTRACT,
          quantity: currenyAmount, //"1.0000 OPN"
          memo: memo,
        },
      },
    ],
  });
};

export const sendWrappedCurrency = async (
  privateKey,
  sender,
  reciever,
  quantity,
  ticker
) => {
  const { eos } = getEos(privateKey);

  return await eos.transaction({
    actions: [
      {
        account: MIDL_CONTRACT,
        name: "deposit",
        authorization: [
          {
            actor: sender,
            permission: "active",
          },
        ],
        data: {
          ticker_name: ticker,
          from: sender,
          to: reciever,
          quantity: quantity,
          memo: "deposit",
        },
      },
    ],
  });
};

export const getWrappedCurrencies = async (username) => {
  const { eosapi } = getEos();

  let wrappedCurrencies = [];

  await eosapi
    .getTableRows({
      json: true,
      code: "midl.token",
      scope: username,
      table: "accounts",
      limit: 20,
      reverse: false,
      show_payer: false,
    })
    .then((res) => {
      wrappedCurrencies = res.rows;
    })
    .catch((err) => {
      wrappedCurrencies = [];
    });

  return wrappedCurrencies;
};
export const successTransactionAction = async (name, rpc, requestId) => {
  const { eos } = rpc;
  const transactionData = {
    actions: [
      {
        account: CONTRACT_NAME,
        name: "fullfilldep",
        authorization: [
          {
            actor: name,
            permission: "active",
          },
        ],
        data: {
          reqid: requestId,
          caller: name,
        },
      },
    ],
  };
  try {
    const result = await eos.transaction(transactionData);
    return result;
  } catch (err) {
    console.error("error", err);
    handleTxCallError(err);
  }
};

export const cancelOrderAction = async (name, rpc, requestId) => {
  const { eos } = rpc;
  const transactionData = {
    actions: [
      {
        account: CONTRACT_NAME,
        name: "cancelreq",
        authorization: [
          {
            actor: name,
            permission: "active",
          },
        ],
        data: {
          reqid: requestId,
          user: name,
        },
      },
    ],
  };
  try {
    const result = await eos.transaction(transactionData);
    return result;
  } catch (err) {
    console.error("error", err);
    handleTxCallError(err);
  }
};

export const successWithdrawAction = async (name, rpc, requestId) => {
  const { eos } = rpc;
  const transactionData = {
    actions: [
      {
        account: CONTRACT_NAME,
        name: "fullfillwit",
        authorization: [
          {
            actor: name,
            permission: "active",
          },
        ],
        data: {
          reqid: requestId,
          caller: name,
        },
      },
    ],
  };
  try {
    const result = await eos.transaction(transactionData);
    if (result.processed.receipt.status == "executed") {
      return result;
    }
  } catch (err) {
    console.error("error", err);
    handleTxCallError(err);
  }
};

export const getUnfulfilledWithdraw = async (name, rpc) => {
  const { eosapi } = rpc;
  try {
    const result = await eosapi.getTableRows({
      json: true,
      code: CONTRACT_NAME,
      scope: CONTRACT_NAME,
      table: "withdraw",
      limit: 100000,
      reverse: false,
      index_position: 0,
      key_type: "i64",
      show_payer: false,
      lower_bound: name,
      upper_bound: name,
    });
    if (result.rows.length > 0) {
      return parseFloat(result.rows[0].amount.split(" ")[0]);
    }
  } catch (err) {
    console.error("error", err);
    return null;
  }
};

export const getTribunalEarn = async (name, rpc) => {
  const { eosapi } = rpc;
  try {
    const result = await eosapi.getTableRows({
      json: true,
      code: CONTRACT_NAME,
      scope: CONTRACT_NAME,
      table: "tribearn",
      limit: 100000,
      reverse: false,
      index_position: 0,
      key_type: "i64",
      show_payer: false,
      lower_bound: name,
      upper_bound: name,
    });
    if (result.rows.length > 0) {
      return result.rows[0];
    }
  } catch (err) {
    console.error("error on getTribunalEarn", err);
    return null;
  }
};

export const claimTribunalEarn = async (amount, rpc, username) => {
  const { eos } = rpc;
  try {
    const result = await eos.transaction({
      actions: [
        {
          account: CONTRACT_NAME,
          name: "tribclaim",
          authorization: [
            {
              actor: username,
              permission: "active",
            },
          ],
          data: {
            amount: `${Number(amount).toFixed(4)} OPN`,
          },
        },
      ],
    });
    if (result.processed.receipt.status == "executed") {
      return result;
    }
  } catch (err) {
    console.error("error on claim tribunal", err);
    handleTxCallError(err);
  }
};

/////////////////////////
//Get Disputes
//////////////////////
export const getDisputes = async (id, rpc) => {
  const { eosapi } = rpc;

  try {
    const res = await eosapi.getTableRows({
      code: CONTRACT_NAME,
      scope: CONTRACT_NAME,
      table: "disputes",
      limit: 100000,
      json: true,
      lower_bound: id,
      upper_bound: id,
    });
    if (res.rows.length) return res.rows[0];
  } catch (error) {
    console.log("Exception at get Disputes", error);
  }
};
export const userClaimAction = async (amount, rpc, username) => {
  const { eos } = rpc;
  console.log("amount", amount);
  return await eos.transaction({
    actions: [
      {
        account: CONTRACT_NAME,
        name: "userclaim",
        authorization: [
          {
            actor: username,
            permission: "active",
          },
        ],
        data: {
          user: username,
          quantity: amount,
          offset: 0,
        },
      },
    ],
  });
};

export const custClaimAction = async (amount, rpc, username) => {
  const { eos } = rpc;
  console.log("amount", amount);

  return await eos.transaction({
    actions: [
      {
        account: CONTRACT_NAME,
        name: "custclaim",
        authorization: [
          {
            actor: username,
            permission: "active",
          },
        ],
        data: {
          user: username,
          quantity: amount,
          offset: 4,
        },
      },
    ],
  });
};

export const claimAction = async (amount, rpc, username, currency) => {
  const { eos } = rpc;
  try {
    const result = eos.transaction({
      actions: [
        {
          account: ESCROW_CONTRACT_NAME,
          name: "claim",
          authorization: [
            {
              actor: username,
              permission: "active",
            },
          ],
          data: {
            foruser: username,
            currency_name: currency,
            asset: amount,
          },
        },
      ],
    });
    if (result.processed.receipt.status === "executed") {
      return result;
    }
  } catch (err) {
    console.log("Exception at claim", err);
    handleTxCallError(err);
  }
};

export const rampClaimAction = async (amount, rpc, username, currency) => {
  const { eos } = rpc;
  try {
    const result = eos.transaction({
      actions: [
        {
          account: ESCROW_CONTRACT_NAME,
          name: "rampclaim",
          authorization: [
            {
              actor: username,
              permission: "active",
            },
          ],
          data: {
            foruser: username,
            currency_name: currency,
            asset: amount,
          },
        },
      ],
    });
    if (result.processed.receipt.status === "executed") {
      return result;
    }
  } catch (err) {
    console.log("Exception at claim", err);
    handleTxCallError(err);
  }
};
